/**
 * Utilidades de WhatsApp (wa.me) para invitaciones y respuestas a incidencias.
 *
 * Fase 0C: las altas de residentes y los tokens/links de activación los arma
 * el servidor (callables inviteResident y residentAccountAction de
 * functions/identity, vía src/utils/identityApi.js). Ya no quedan acá
 * createResidentInvitation, createResidentDirect ni generateActivationToken/Link.
 */
import { normalizePhoneUY, toWaMeDigits } from "./phoneE164";

/**
 * Normaliza el celular a formato E.164 para WhatsApp.
 * Re-exporta normalizePhoneUY de utils/phoneE164.js para mantener compat con
 * callsites que importaban normalizePhoneForWhatsApp desde aca.
 */
export const normalizePhoneForWhatsApp = normalizePhoneUY;

/**
 * Envía WhatsApp de bienvenida con link de activación.
 * Abre WhatsApp (wa.me) con el número y el mensaje listos; el usuario solo tiene que hacer clic en Enviar.
 * El número se normaliza a E.164 (ej. 099366768 → +59899366768).
 * @returns {Promise<boolean>} true si se abrió WhatsApp correctamente.
 */
export const sendWelcomeWhatsApp = async (nombre, celular, activationLink) => {
  try {
    const toNumber = normalizePhoneForWhatsApp(celular);
    let message = `¡Hola ${nombre}! 👋\n\nLa comisión de tu edificio te invitó a usar Habitia, la plataforma para gestionar todo lo relacionado a tu edificio.\n\nActivá tu cuenta en este link seguro:\n\n${activationLink}\n\nTe solicitaremos foto de documento de compraventa o contrato de alquiler.\n\n(Link válido 24/48 hs, regenerable si caduca)`;
    message = message.replace(/propit/gi, "Habitia");

    if (!toNumber) {
      console.warn("No se pudo normalizar el celular. Usá formato 099366768 o +59899366768.");
      return false;
    }

    // wa.me: número sin el + (ej. 59899366768)
    const digitsOnly = toWaMeDigits(toNumber);
    const url = `https://wa.me/${digitsOnly}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");

    return true;
  } catch (error) {
    console.error("Error abriendo WhatsApp:", error);
    return false;
  }
};

/**
 * Abre WhatsApp (wa.me) con la respuesta al residente sobre su incidencia.
 * El admin confirma el envío en la app de WhatsApp.
 * @param {{ nombre?: string, celular: string, incidentTitle: string, message: string }} params
 * @returns {Promise<boolean>}
 */
export const sendIncidentClientWhatsApp = async ({
  nombre,
  celular,
  incidentTitle,
  message,
}) => {
  try {
    const toNumber = normalizePhoneForWhatsApp(String(celular || "").trim());
    const body = (message || "").trim();
    if (!toNumber || !body) {
      console.warn("WhatsApp incidencia: falta número o mensaje.");
      return false;
    }
    const title = (incidentTitle || "Tu incidencia").trim();
    const text = `Hola${nombre ? ` ${nombre}` : ""}\n\nTe escribimos desde Habitia sobre tu incidencia:\n"${title}"\n\n${body}\n\n— Administración del edificio`;
    const digitsOnly = toWaMeDigits(toNumber);
    const url = `https://wa.me/${digitsOnly}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    return true;
  } catch (error) {
    console.error("Error abriendo WhatsApp (incidencia):", error);
    return false;
  }
};
